import React from "react"
import { Field as FieldPrimitive } from "@base-ui/react/field"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../utils"
import { FieldContext } from "./field-context"

// =============================================================================
// Styles
// =============================================================================
const DEFAULT_ORIENTATION = "vertical" as const

// A disabled field keeps its label legible so it can say why. Only a lone
// checkbox or radio grays it, because that label is the option's value. The
// rule reads off the field rather than a sibling, because the two orientations
// put the control on either side of the label. A field holding items grays
// nothing: each `Field.Item` owns its label.
const fieldStyles = cva(
  [
    "flex gap-2",
    "not-has-[[data-slot=field-item]]:has-[[data-slot=checkbox][data-disabled],[data-slot=radio][data-disabled]]:[&_[data-slot=field-label]]:text-gray-600",
    "not-has-[[data-slot=field-item]]:has-[[data-slot=checkbox][data-disabled],[data-slot=radio][data-disabled]]:[&_[data-slot=field-description]]:text-gray-600"
  ],
  {
    variants: {
      orientation: {
        vertical: "flex-col",
        // A select's trigger is taller than the label's line, so the two centre
        horizontal: "items-start has-[[data-slot=select-trigger]]:items-center"
      }
    },
    defaultVariants: {
      orientation: DEFAULT_ORIENTATION
    }
  }
)

export type FieldOrientation = NonNullable<
  VariantProps<typeof fieldStyles>["orientation"]
>

// =============================================================================
// Props
// =============================================================================
export type FieldRootProps = {
  /** A control and its `Field.Label`, in the order they should appear */
  children?: React.ReactNode
  /** Identifies the field when its form is submitted */
  name?: string
  /**
   * `vertical` stacks the label above the control (text inputs, selects).
   * `horizontal` sits them side by side (checkboxes, radios, toggles, or a
   * select in a toolbar).
   */
  orientation?: FieldOrientation
  /**
   * Disables the control. Only a lone `Checkbox` or `Radio` grays the label.
   *
   * Setting it here or on the control does the same thing: the two are OR'd,
   * so neither overrides the other.
   */
  disabled?: boolean
  /**
   * Marks the value as rejected: the control gets `aria-invalid` and
   * `Field.Error` shows. Set it from wherever the error lives, for example the
   * surrounding form's error for this field.
   */
  invalid?: boolean
  /** Additional CSS classes for layout */
  className?: string
}

// =============================================================================
// Component
// =============================================================================
/**
 * Groups a form control with its label and owns the state they share.
 *
 * The control registers its id with the field, so `Field.Label` resolves its
 * own `htmlFor` and the control gets an accessible name without either side
 * being wired up by hand.
 */
function Field({
  children,
  name,
  orientation = DEFAULT_ORIENTATION,
  disabled,
  invalid = false,
  className
}: FieldRootProps) {
  const labelId = React.useId()

  return (
    <FieldContext.Provider value={{ invalid, labelId, disabled: !!disabled }}>
      <FieldPrimitive.Root
        data-slot="field"
        name={name}
        // Published so a part can style off the orientation without knowing
        // which classes produced it, the way Base UI projects its own state.
        data-orientation={orientation}
        disabled={disabled}
        invalid={invalid}
        className={cn(fieldStyles({ orientation }), className)}
      >
        {children}
      </FieldPrimitive.Root>
    </FieldContext.Provider>
  )
}

Field.displayName = "Field"

export { Field as FieldRoot }
